// src/components/calendar/LessonForm.tsx

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    FormHelperText,
    InputAdornment,
    Alert,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    FormLabel,
    Checkbox,
    Chip,
} from '@mui/material';
import {
    Phone as PhoneIcon,
    Link as LinkIcon,
    Assignment as AssignmentIcon,
    AccessTime as AccessTimeIcon,
    CalendarToday as CalendarIcon,
    Repeat as RepeatIcon,
    CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import { format, isValid, differenceInDays } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { Student, Group, CreateLessonDto, Lesson, RepeatType, UpdateLessonSeriesDto } from '../../types';
import { getUserTimeZone, convertLessonTime } from '../../utils/dateUtils';

export interface LessonFormRef {
    submit: () => void;
}

interface LessonFormProps {
    initialData?: CreateLessonDto | null;
    selectedDate?: Date;
    students: Student[];
    groups: Group[];
    lessons: Lesson[];
    editingLessonId?: number | null;
    editingSeriesId?: number | null;
    isSeriesEdit?: boolean;
    onSubmit: (data: CreateLessonDto | UpdateLessonSeriesDto) => void;
    loading?: boolean;
}

const LessonForm = forwardRef<LessonFormRef, LessonFormProps>(({
                                                                   initialData,
                                                                   selectedDate,
                                                                   students,
                                                                   groups,
                                                                   lessons,
                                                                   editingLessonId,
                                                                   editingSeriesId,
                                                                   isSeriesEdit,
                                                                   onSubmit,
                                                                   loading = false,
                                                               }, ref) => {
    const userTimeZone = getUserTimeZone();

    // Локальная дата/время
    const [localDate, setLocalDate] = useState<string>('');
    const [startTime, setStartTime] = useState<string>('09:00');
    const [endTime, setEndTime] = useState<string>('10:00');

    const [formData, setFormData] = useState<CreateLessonDto>({
        studentId: undefined,
        groupId: undefined,
        date: '',
        startTime: '09:00',
        endTime: '10:00',
        timezone: userTimeZone,
        topic: '',
        recordingLink: null,
        homeworkLink: null,
        isFree: false,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [lessonType, setLessonType] = useState<'individual' | 'group'>('individual');
    const [isFree, setIsFree] = useState(false);
    const [applyToAll, setApplyToAll] = useState(false);

    // Repeat
    const [repeatEnabled, setRepeatEnabled] = useState(false);
    const [repeatType, setRepeatType] = useState<RepeatType>('WEEKLY');
    const [repeatUntil, setRepeatUntil] = useState<string>('');

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    // Авто-отметка "Применить ко всем" при редактировании серии
    useEffect(() => {
        if (isSeriesEdit) {
            setApplyToAll(true);
        } else {
            setApplyToAll(false);
        }
    }, [isSeriesEdit]);

    // Инициализация
    useEffect(() => {
        if (initialData) {
            // РЕДАКТИРОВАНИЕ — берём isFree из данных
            setLocalDate(utcToLocalDate(initialData.date));
            setStartTime(initialData.startTime?.slice(0, 5) || '09:00');
            setEndTime(initialData.endTime?.slice(0, 5) || '10:00');
            setIsFree(initialData.isFree || false);   // ← из initialData
            setFormData(prev => ({
                ...prev,
                studentId: initialData.studentId,
                groupId: initialData.groupId,
                date: initialData.date,
                startTime: initialData.startTime || '09:00',
                endTime: initialData.endTime || '10:00',
                timezone: initialData.timezone || userTimeZone,
                topic: initialData.topic || '',
                recordingLink: initialData.recordingLink || null,
                homeworkLink: initialData.homeworkLink || null,
                isFree: initialData.isFree || false,
            }));
            if (initialData.groupId) {
                setLessonType('group');
            } else {
                setLessonType('individual');
            }
        } else if (selectedDate) {
            // СОЗДАНИЕ — сбрасываем isFree
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            setLocalDate(dateStr);
            const now = new Date();
            const hours = String(now.getHours() + 1).padStart(2, '0');
            const start = `${hours}:00`;
            const end = `${String(Number(hours) + 1).padStart(2, '0')}:00`;
            setStartTime(start);
            setEndTime(end);

            const defaultUntil = new Date(selectedDate);
            defaultUntil.setMonth(defaultUntil.getMonth() + 3);
            setRepeatUntil(format(defaultUntil, 'yyyy-MM-dd'));

            setIsFree(false);   // ← только для создания
        } else {
            // СОЗДАНИЕ — сбрасываем isFree
            setLocalDate(getCurrentLocalDate());
            const now = new Date();
            const hours = String(now.getHours() + 1).padStart(2, '0');
            setStartTime(`${hours}:00`);
            setEndTime(`${String(Number(hours) + 1).padStart(2, '0')}:00`);

            const defaultUntil = new Date();
            defaultUntil.setMonth(defaultUntil.getMonth() + 3);
            setRepeatUntil(format(defaultUntil, 'yyyy-MM-dd'));

            setIsFree(false);   // ← только для создания
        }

        setRepeatEnabled(false);
        setRepeatType('WEEKLY');
        setErrors({});
    }, [initialData, selectedDate]);

    // Синхронизация formData
    useEffect(() => {
        if (localDate) {
            const utcDate = localDateToUTC(localDate);
            setFormData(prev => ({ ...prev, date: utcDate }));
        }
    }, [localDate]);

    useEffect(() => {
        setFormData(prev => ({ ...prev, startTime, endTime }));
    }, [startTime, endTime]);

    useEffect(() => {
        setFormData(prev => ({ ...prev, isFree }));
    }, [isFree]);

    // ===== ХЕЛПЕРЫ ДЛЯ ДАТ =====
    const localDateToUTC = (localDateStr: string): string => {
        if (!localDateStr) return '';
        try {
            const date = new Date(localDateStr);
            if (!isValid(date)) return '';
            return format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'");
        } catch {
            return '';
        }
    };

    const utcToLocalDate = (utcStr: string): string => {
        if (!utcStr) return '';
        try {
            const date = new Date(utcStr);
            if (!isValid(date)) return '';
            return format(date, 'yyyy-MM-dd');
        } catch {
            return '';
        }
    };

    const getCurrentLocalDate = (): string => {
        return format(new Date(), 'yyyy-MM-dd');
    };

    const convertLocalTimeToLessonTimeZone = (
        localTime: string,
        lessonTimeZone: string,
        date: Date
    ): string => {
        try {
            if (userTimeZone === lessonTimeZone) {
                return localTime;
            }

            const getOffset = (tz: string): number => {
                const fmt = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    hour12: false,
                });
                const parts = fmt.formatToParts(date);
                const get = (t: string) => parts.find(p => p.type === t)?.value || '0';
                const tzDateAsUTC = Date.UTC(
                    parseInt(get('year')),
                    parseInt(get('month')) - 1,
                    parseInt(get('day')),
                    parseInt(get('hour')) === 24 ? 0 : parseInt(get('hour')),
                    parseInt(get('minute')),
                    parseInt(get('second'))
                );
                return Math.round((tzDateAsUTC - date.getTime()) / 60000);
            };

            const userOffset = getOffset(userTimeZone);
            const lessonOffset = getOffset(lessonTimeZone);
            const diffMinutes = lessonOffset - userOffset;

            if (diffMinutes === 0) return localTime;

            const [hours, minutes] = localTime.split(':').map(Number);
            let totalMinutes = hours * 60 + minutes + diffMinutes;
            totalMinutes = ((totalMinutes % 1440) + 1440) % 1440;

            const newHours = Math.floor(totalMinutes / 60);
            const newMinutes = totalMinutes % 60;
            return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
        } catch {
            return localTime;
        }
    };

    // ===== ОБРАБОТЧИКИ =====
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalDate(value);
        if (errors.date) setErrors(prev => ({ ...prev, date: '' }));
    };

    const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStartTime(e.target.value);
        if (errors.startTime) setErrors(prev => ({ ...prev, startTime: '' }));
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEndTime(e.target.value);
        if (errors.endTime) setErrors(prev => ({ ...prev, endTime: '' }));
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleStudentChange = (e: SelectChangeEvent<number>) => {
        const value = e.target.value as number;
        setFormData(prev => ({ ...prev, studentId: value, groupId: undefined }));
        if (errors.studentId) setErrors(prev => ({ ...prev, studentId: '' }));
    };

    const handleGroupChange = (e: SelectChangeEvent<number>) => {
        const value = e.target.value as number;
        setFormData(prev => ({ ...prev, groupId: value, studentId: undefined }));
        if (errors.groupId) setErrors(prev => ({ ...prev, groupId: '' }));
    };

    const handleLessonTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as 'individual' | 'group';
        setLessonType(value);
        if (value === 'individual') {
            setFormData(prev => ({ ...prev, groupId: undefined }));
        } else {
            setFormData(prev => ({ ...prev, studentId: undefined }));
        }
    };

    // ===== ПОДСЧЁТ ПОВТОРОВ =====
    const countRepeatLessons = (): number => {
        if (!repeatEnabled || !localDate || !repeatUntil) return 0;
        try {
            const start = new Date(localDate);
            const end = new Date(repeatUntil);
            if (end < start) return 0;
            const daysDiff = differenceInDays(end, start);
            switch (repeatType) {
                case 'DAILY': return daysDiff + 1;
                case 'WEEKLY': return Math.floor(daysDiff / 7) + 1;
                case 'BIWEEKLY': return Math.floor(daysDiff / 14) + 1;
                case 'MONTHLY': {
                    const months = (end.getFullYear() - start.getFullYear()) * 12
                        + (end.getMonth() - start.getMonth());
                    return months + 1;
                }
                default: return 0;
            }
        } catch {
            return 0;
        }
    };

    const getDayOfWeekName = (): string => {
        if (!localDate) return '';
        try {
            const date = new Date(localDate);
            const days = ['воскресенье', 'понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу'];
            return days[date.getDay()];
        } catch {
            return '';
        }
    };

    const getDayOfMonth = (): number => {
        if (!localDate) return 0;
        try {
            return new Date(localDate).getDate();
        } catch {
            return 0;
        }
    };

    const getRepeatDescription = (): string => {
        if (!repeatEnabled) return '';
        switch (repeatType) {
            case 'DAILY': return `Каждый день в это же время`;
            case 'WEEKLY': return `Каждый ${getDayOfWeekName()} в это же время`;
            case 'BIWEEKLY': return `Каждые 2 недели по ${getDayOfWeekName()} в это же время`;
            case 'MONTHLY': return `${getDayOfMonth()}-го числа каждого месяца в это же время`;
            default: return '';
        }
    };

    // ===== ВАЛИДАЦИЯ (без проверки пересечений) =====
    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!localDate) newErrors.date = 'Выберите дату';
        if (!startTime) newErrors.startTime = 'Выберите время начала';
        if (!endTime) newErrors.endTime = 'Выберите время окончания';

        // Проверка перехода через полночь
        if (startTime && endTime) {
            const [startH, startM] = startTime.split(':').map(Number);
            const [endH, endM] = endTime.split(':').map(Number);
            const startMin = startH * 60 + startM;
            let endMin = endH * 60 + endM;
            if (endMin <= startMin) endMin += 24 * 60;
            if (endMin - startMin > 24 * 60) {
                newErrors.endTime = 'Урок не может длиться больше 24 часов';
            }
        }

        if (!formData.topic || formData.topic.trim().length < 2) {
            newErrors.topic = 'Тема урока обязательна (минимум 2 символа)';
        }

        if (lessonType === 'individual' && !formData.studentId) {
            newErrors.studentId = 'Выберите ученика';
        }

        if (lessonType === 'group' && !formData.groupId) {
            newErrors.groupId = 'Выберите группу';
        }

        if (repeatEnabled && !isSeriesEdit) {
            if (!repeatUntil) {
                newErrors.repeatUntil = 'Выберите дату окончания повтора';
            } else {
                const start = new Date(localDate);
                const end = new Date(repeatUntil);
                if (end < start) {
                    newErrors.repeatUntil = 'Дата окончания должна быть позже даты начала';
                } else {
                    const count = countRepeatLessons();
                    if (count > 365) {
                        newErrors.repeatUntil = `Серия не может содержать больше 365 уроков. Сейчас: ${count}`;
                    }
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ===== SUBMIT =====
    const handleSubmit = () => {
        if (!validate()) return;

        const targetTimeZone = initialData?.timezone || userTimeZone;
        const lessonDate = formData.date ? new Date(formData.date) : new Date();

        const finalStartTime = convertLocalTimeToLessonTimeZone(startTime, targetTimeZone, lessonDate);
        const finalEndTime = convertLocalTimeToLessonTimeZone(endTime, targetTimeZone, lessonDate);

        // ===== РЕДАКТИРОВАНИЕ СЕРИИ =====
        if (isSeriesEdit && applyToAll) {
            const seriesData: UpdateLessonSeriesDto = {
                startTime: `${finalStartTime}:00`,
                endTime: `${finalEndTime}:00`,
                topic: formData.topic.trim(),
                date: formData.date,
                timezone: initialData?.timezone || userTimeZone,
                isFree,
                applyTo: 'future',
            };

            if (lessonType === 'individual') {
                seriesData.studentId = formData.studentId || null;
                seriesData.groupId = null;
            } else {
                seriesData.groupId = formData.groupId || null;
                seriesData.studentId = null;
            }

            onSubmit(seriesData);
            return;
        }

        // ===== СОЗДАНИЕ ИЛИ РЕДАКТИРОВАНИЕ ОДИНОЧНОГО УРОКА =====
        const cleanData: CreateLessonDto = {
            studentId: formData.studentId,
            groupId: formData.groupId,
            date: formData.date,
            startTime: finalStartTime,
            endTime: finalEndTime,
            timezone: targetTimeZone,
            topic: formData.topic.trim(),
            recordingLink: formData.recordingLink?.trim() || null,
            homeworkLink: formData.homeworkLink?.trim() || null,
            isFree,
        };

        if (!initialData && repeatEnabled) {
            cleanData.repeat = {
                enabled: true,
                type: repeatType,
                until: repeatUntil,
            };
        }

        onSubmit(cleanData);
    };

    const availableStudents = students.filter(s => s.isActive);
    const availableGroups = groups;

    return (
        <Box sx={{ mt: 1 }}>
            {initialData && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Редактирование урока
                </Alert>
            )}

            <Grid container spacing={2.5}>
                {/* Тип урока */}
                <Grid size={{ xs: 12 }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Тип урока</FormLabel>
                        <RadioGroup
                            row
                            value={lessonType}
                            onChange={handleLessonTypeChange}
                        >
                            <FormControlLabel
                                value="individual"
                                control={<Radio />}
                                label="Индивидуальный"
                            />
                            <FormControlLabel
                                value="group"
                                control={<Radio />}
                                label="Групповой"
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {/* Ученик/группа */}
                {lessonType === 'individual' ? (
                    <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth error={!!errors.studentId}>
                            <InputLabel>Ученик</InputLabel>
                            <Select
                                value={formData.studentId || ''}
                                onChange={handleStudentChange}
                                label="Ученик"
                                disabled={loading}
                            >
                                {availableStudents.length === 0 ? (
                                    <MenuItem disabled>
                                        <Typography variant="body2" color="text.secondary">
                                            Нет доступных учеников
                                        </Typography>
                                    </MenuItem>
                                ) : (
                                    availableStudents.map((student) => (
                                        <MenuItem key={student.id} value={student.id}>
                                            {student.fullName} ({student.pricePerLesson} ₽)
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {errors.studentId && (
                                <FormHelperText>{errors.studentId}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                ) : (
                    <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth error={!!errors.groupId}>
                            <InputLabel>Группа</InputLabel>
                            <Select
                                value={formData.groupId || ''}
                                onChange={handleGroupChange}
                                label="Группа"
                                disabled={loading}
                            >
                                {availableGroups.length === 0 ? (
                                    <MenuItem disabled>
                                        <Typography variant="body2" color="text.secondary">
                                            Нет доступных групп
                                        </Typography>
                                    </MenuItem>
                                ) : (
                                    availableGroups.map((group) => (
                                        <MenuItem key={group.id} value={group.id}>
                                            {group.name} ({group.studentIds?.length || 0} уч.)
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                            {errors.groupId && (
                                <FormHelperText>{errors.groupId}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                )}

                {/* Дата */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        fullWidth
                        required
                        label="Дата"
                        type="date"
                        value={localDate}
                        onChange={handleDateChange}
                        error={!!errors.date}
                        helperText={errors.date || `В вашем часовом поясе (${userTimeZone})`}
                        disabled={loading}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Время начала */}
                <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                        fullWidth
                        required
                        label="Начало"
                        type="time"
                        value={startTime}
                        onChange={handleStartTimeChange}
                        error={!!errors.startTime}
                        helperText={errors.startTime}
                        disabled={loading}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccessTimeIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Время окончания */}
                <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                        fullWidth
                        required
                        label="Окончание"
                        type="time"
                        value={endTime}
                        onChange={handleEndTimeChange}
                        error={!!errors.endTime}
                        helperText={errors.endTime}
                        disabled={loading}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccessTimeIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Тема */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Тема урока"
                        name="topic"
                        value={formData.topic}
                        onChange={handleTextChange}
                        error={!!errors.topic}
                        helperText={errors.topic}
                        placeholder="Алгебра: квадратные уравнения"
                        disabled={loading}
                    />
                </Grid>

                {/* Бесплатный урок */}
                <Grid size={{ xs: 12 }}>
                    <Box sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: isFree ? 'success.light' + '15' : 'action.hover',
                        border: '1px solid',
                        borderColor: isFree ? 'success.main' : 'divider',
                        transition: 'all 0.2s',
                    }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isFree}
                                    onChange={(e) => setIsFree(e.target.checked)}
                                    disabled={loading}
                                    color="success"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Бесплатный урок
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {isFree
                                            ? 'Урок не будет учитываться в статистике доходов'
                                            : 'Пробное занятие, тестовый экзамен, бонус и т.п.'}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </Grid>

                {/* Повтор (только при создании, не для серии) */}
                {!initialData && !isSeriesEdit && (
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />

                        <Box sx={{
                            p: 2,
                            borderRadius: 1,
                            bgcolor: repeatEnabled ? 'primary.light' + '15' : 'action.hover',
                            border: '1px solid',
                            borderColor: repeatEnabled ? 'primary.main' : 'divider',
                            transition: 'all 0.2s',
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={repeatEnabled}
                                        onChange={(e) => setRepeatEnabled(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RepeatIcon color={repeatEnabled ? 'primary' : 'action'} />
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            Повторять урок
                                        </Typography>
                                    </Box>
                                }
                            />

                            {repeatEnabled && (
                                <Box sx={{ mt: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormControl fullWidth size="small" disabled={loading}>
                                                <InputLabel>Тип повторения</InputLabel>
                                                <Select
                                                    value={repeatType}
                                                    onChange={(e) => setRepeatType(e.target.value as RepeatType)}
                                                    label="Тип повторения"
                                                >
                                                    <MenuItem value="DAILY">Ежедневно</MenuItem>
                                                    <MenuItem value="WEEKLY">Еженедельно</MenuItem>
                                                    <MenuItem value="BIWEEKLY">Каждые 2 недели</MenuItem>
                                                    <MenuItem value="MONTHLY">Ежемесячно</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth
                                                required
                                                label="Повторять до"
                                                type="date"
                                                value={repeatUntil}
                                                onChange={(e) => {
                                                    setRepeatUntil(e.target.value);
                                                    if (errors.repeatUntil) {
                                                        setErrors(prev => ({ ...prev, repeatUntil: '' }));
                                                    }
                                                }}
                                                error={!!errors.repeatUntil}
                                                helperText={errors.repeatUntil}
                                                disabled={loading}
                                                size="small"
                                                slotProps={{
                                                    inputLabel: { shrink: true },
                                                    htmlInput: {
                                                        min: localDate || format(new Date(), 'yyyy-MM-dd'),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            {getRepeatDescription()}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`Будет создано: ${countRepeatLessons()} уроков`}
                                                size="small"
                                                color={countRepeatLessons() > 365 ? 'error' : countRepeatLessons() > 100 ? 'warning' : 'primary'}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                )}

                {/* Редактирование серии */}
                {isSeriesEdit && (
                    <Grid size={{ xs: 12 }}>
                        <Alert severity="info" icon={<RepeatIcon />}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Это урок из серии повторяющихся уроков
                            </Typography>
                            <Typography variant="caption">
                                Изменения будут применены ко <strong>всем будущим урокам серии</strong>.
                                Прошедшие уроки останутся без изменений.
                            </Typography>
                        </Alert>
                    </Grid>
                )}

                <Divider sx={{ width: '100%' }} />

                {/* Ссылка на запись */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Ссылка на запись урока"
                        name="recordingLink"
                        value={formData.recordingLink || ''}
                        onChange={handleTextChange}
                        placeholder="https://zoom.us/rec/..."
                        disabled={loading}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LinkIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>

                {/* Ссылка на ДЗ */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Ссылка на домашнее задание"
                        name="homeworkLink"
                        value={formData.homeworkLink || ''}
                        onChange={handleTextChange}
                        placeholder="https://drive.google.com/..."
                        disabled={loading}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AssignmentIcon color="action" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
});

LessonForm.displayName = 'LessonForm';

export default LessonForm;